<section class="component-page" *transloco="let t">
  <app-introduction-box
    [title]="'menu.policies'"
    [description]="'policy.overview.introductionText'"
    [hidable]="true"
    [imageSrc]="'assets/images/asset-introduction-box.png'"
    [imageAlt]="'Image'"
  >
    <ng-template let-hidden>
      <app-button
        [label]="'policy.overview.button.create.label' | transloco"
        [matIcon]="'add'"
        [iconPosition]="'end'"
        (click)="openCreatePolicyPage()"
        (keydown.enter)="$event.stopPropagation()"
        (keyup.space)="$event.stopPropagation()"
      ></app-button>
    </ng-template>
  </app-introduction-box>
  @if (sortedPolicies().length) {
    <div class="cards-toolbar">
      <span class="cards-count">{{ sortedPolicies().length }} {{ t(sortedPolicies().length === 1 ? 'sort.item' : 'sort.items') }}</span>
      <app-sort-dropdown
        [options]="sortOptions"
        [selectedField]="sortField()"
        [selectedDirection]="sortDirection()"
        (sortChanged)="onSortChanged($event)" />
    </div>
    <div class="entity-cards-wrapper">
      @for (policy of sortedPolicies(); track policy.id) {
        <app-policy-overview-card
          [policy]="policy"
          [showDelete]="false"
          (titleClick)="openPolicyDetailPage(policy)"
        />
      }
    </div>
  } @else {
    <div class="empty-state">
      <div class="empty-state-icon-ring">
        <mat-icon class="empty-state-icon">verified_user</mat-icon>
      </div>
      <span class="empty-state-heading">{{ t('policies.emptyState.title') }}</span>
      <span class="empty-state-description">{{ t('policies.emptyState.description') }}</span>
    </div>
  }
</section>

<!-- ═══════════════════════════════════════════════════════════ -->
<!--  Slide-Over Panel: Flat Policy Detail Body                 -->
<!-- ═══════════════════════════════════════════════════════════ -->
<ng-template #panelBody>
  @if (panelPolicy(); as policy) {

    <!-- Description -->
    @if (panelDescription()) {
      <div class="so-description">{{ panelDescription() }}</div>
      <hr class="so-divider" />
    }

    <!-- General Information -->
    <p class="so-section-label">
      <mat-icon class="so-section-icon" fontSet="material-icons-outlined">info</mat-icon>
      {{ 'policy.detail.block.general' | transloco }}
    </p>
    <div class="so-field-grid">
      <div class="so-field so-field--full">
        <span class="so-field-label">{{ 'policy.detail.block.general.id' | transloco }}</span>
        <span class="so-field-value so-field-value--mono">{{ policy.id }}</span>
      </div>
      <div class="so-field">
        <span class="so-field-label">{{ 'policy.detail.block.general.createdAt' | transloco }}</span>
        <span class="so-field-value">{{ panelCreatedAt() }}</span>
      </div>
    </div>

    <!-- Permissions -->
    @if (panelPermissionCards().length) {
      <hr class="so-divider" />
      <p class="so-section-label">
        <mat-icon class="so-section-icon" fontSet="material-icons-outlined">check_circle</mat-icon>
        {{ 'policy.detail.block.permissions' | transloco }}
      </p>
      <div class="so-policy-list">
        @for (card of panelPermissionCards(); track $index) {
          <div class="so-policy-item so-policy-item--{{ card.severity }}">
            <div class="so-policy-header">
              <mat-icon class="so-policy-icon" fontSet="material-icons-outlined">{{ card.icon }}</mat-icon>
              <span class="so-policy-title">{{ card.title }}</span>
              <span class="so-severity-badge so-severity-badge--{{ card.severity }}">
                <mat-icon class="so-badge-icon" fontSet="material-icons-outlined">{{ card.badgeIcon }}</mat-icon>
                {{ card.typeBadge }}
              </span>
            </div>
            @if (card.explanation) {
              <span class="so-policy-explanation">{{ card.explanation }}</span>
            }
            @if (card.constraintRows.length) {
              <div class="so-constraint-rows">
                @for (row of card.constraintRows; track $index) {
                  <div class="so-constraint-row">
                    <span class="so-constraint-label">{{ row.label }}</span>
                    <span class="so-constraint-value" [class.so-constraint-value--mono]="row.mono">{{ row.value }}</span>
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>
    }

    <!-- Obligations -->
    @if (panelObligationCards().length) {
      <hr class="so-divider" />
      <p class="so-section-label">
        <mat-icon class="so-section-icon" fontSet="material-icons-outlined">assignment_turned_in</mat-icon>
        {{ 'policy.detail.block.obligations' | transloco }}
      </p>
      <div class="so-policy-list">
        @for (card of panelObligationCards(); track $index) {
          <div class="so-policy-item so-policy-item--{{ card.severity }}">
            <div class="so-policy-header">
              <mat-icon class="so-policy-icon" fontSet="material-icons-outlined">{{ card.icon }}</mat-icon>
              <span class="so-policy-title">{{ card.title }}</span>
              <span class="so-severity-badge so-severity-badge--{{ card.severity }}">
                <mat-icon class="so-badge-icon" fontSet="material-icons-outlined">{{ card.badgeIcon }}</mat-icon>
                {{ card.typeBadge }}
              </span>
            </div>
            @if (card.explanation) {
              <span class="so-policy-explanation">{{ card.explanation }}</span>
            }
            @if (card.constraintRows.length) {
              <div class="so-constraint-rows">
                @for (row of card.constraintRows; track $index) {
                  <div class="so-constraint-row">
                    <span class="so-constraint-label">{{ row.label }}</span>
                    <span class="so-constraint-value" [class.so-constraint-value--mono]="row.mono">{{ row.value }}</span>
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>
    }

    <!-- Prohibitions -->
    @if (panelProhibitionCards().length) {
      <hr class="so-divider" />
      <p class="so-section-label">
        <mat-icon class="so-section-icon" fontSet="material-icons-outlined">block</mat-icon>
        {{ 'policy.detail.block.prohibitions' | transloco }}
      </p>
      <div class="so-policy-list">
        @for (card of panelProhibitionCards(); track $index) {
          <div class="so-policy-item so-policy-item--{{ card.severity }}">
            <div class="so-policy-header">
              <mat-icon class="so-policy-icon" fontSet="material-icons-outlined">{{ card.icon }}</mat-icon>
              <span class="so-policy-title">{{ card.title }}</span>
              <span class="so-severity-badge so-severity-badge--{{ card.severity }}">
                <mat-icon class="so-badge-icon" fontSet="material-icons-outlined">{{ card.badgeIcon }}</mat-icon>
                {{ card.typeBadge }}
              </span>
            </div>
            @if (card.explanation) {
              <span class="so-policy-explanation">{{ card.explanation }}</span>
            }
            @if (card.constraintRows.length) {
              <div class="so-constraint-rows">
                @for (row of card.constraintRows; track $index) {
                  <div class="so-constraint-row">
                    <span class="so-constraint-label">{{ row.label }}</span>
                    <span class="so-constraint-value" [class.so-constraint-value--mono]="row.mono">{{ row.value }}</span>
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>
    }
  }
</ng-template>

<!-- ═══════════════════════════════════════════════════════════ -->
<!--  Slide-Over Panel: Header Actions                           -->
<!-- ═══════════════════════════════════════════════════════════ -->
<ng-template #panelHeaderActions>
  <button
    class="so-header-action-btn"
    (click)="editPolicyFromPanel()"
    aria-label="Bearbeiten">
    <mat-icon>edit</mat-icon>
  </button>
  <button
    class="so-header-action-btn so-header-action-btn--danger"
    (click)="deletePolicyFromPanel()"
    aria-label="Löschen">
    <mat-icon>delete</mat-icon>
  </button>
</ng-template>
